import { useEffect, useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import {
  Autocomplete,
  Box,
  Chip,
  Dialog,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { normalizeString } from "helpers/normalizeString";
import type { ICategoriaProduto } from "interfaces/loja";
import {
  fieldSx,
  modalHeaderSx,
  modalPaperSx,
  saveButtonSx,
  CATEGORIAS_ACCENT,
} from "../categorias.theme";
import { getDescendantIds } from "./categorias.helpers";

type Props = {
  open: boolean;
  editing: ICategoriaProduto | null;
  flatCategories: ICategoriaProduto[];
  initialParentId?: number;
  saving: boolean;
  onClose: () => void;
  onSave: (payload: { nome: string; categoriaPaiId?: number }) => void;
};

export default function CategoriaFormDialog({
  open,
  editing,
  flatCategories,
  initialParentId,
  saving,
  onClose,
  onSave,
}: Props) {
  const [nome, setNome] = useState("");
  const [categoriaPai, setCategoriaPai] = useState<ICategoriaProduto | null>(null);
  const [parentLocked, setParentLocked] = useState(false);

  const parentOptions = useMemo(() => {
    if (!editing) {
      return flatCategories;
    }
    const blocked = new Set([editing.id, ...getDescendantIds(editing)]);
    return flatCategories.filter((c) => !blocked.has(c.id));
  }, [flatCategories, editing]);

  useEffect(() => {
    if (!open) return;
    setNome(editing?.nome ?? "");
    const parentId = editing?.categoriaPaiId ?? initialParentId;
    const parent = parentId
      ? flatCategories.find((c) => c.id === parentId) ?? null
      : null;
    setCategoriaPai(parent);
    setParentLocked(!editing && Boolean(initialParentId));
  }, [open, editing, initialParentId, flatCategories]);

  const slugPreview = nome.trim() ? normalizeString(nome) : "";

  const handleSave = () => {
    if (!nome.trim()) return;
    onSave({
      nome: nome.trim(),
      categoriaPaiId: categoriaPai?.id,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      scroll="paper"
      PaperProps={{ sx: modalPaperSx }}
    >
      <Box sx={modalHeaderSx}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center" minWidth={0}>
            <FolderOutlinedIcon sx={{ fontSize: 36, opacity: 0.9 }} />
            <Box minWidth={0}>
              <Typography variant="overline" sx={{ opacity: 0.75, letterSpacing: 1.2 }}>
                Catálogo da loja
              </Typography>
              <Typography variant="h5" fontWeight="bold" noWrap>
                {editing ? "Editar categoria" : "Nova categoria"}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} sx={{ color: "inherit" }} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <MDBox sx={{ p: 3 }}>
        <Stack spacing={2.5}>
          <TextField
            label="Nome da categoria"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Ex.: Camisetas, Acessórios…"
            fullWidth
            size="small"
            sx={fieldSx}
            autoFocus
          />
          {slugPreview ? (
            <MDTypography variant="caption" color="text.secondary">
              Slug gerado: <strong>{slugPreview}</strong>
            </MDTypography>
          ) : null}

          {parentLocked && categoriaPai ? (
            <Box>
              <MDTypography variant="caption" color="text.secondary" fontWeight="bold">
                Categoria pai
              </MDTypography>
              <Box mt={0.75}>
                <Chip
                  label={categoriaPai.nome}
                  sx={{
                    bgcolor: "rgba(249, 96, 48, 0.12)",
                    color: CATEGORIAS_ACCENT,
                    fontWeight: 600,
                  }}
                />
              </Box>
            </Box>
          ) : (
            <Autocomplete
              value={categoriaPai}
              options={parentOptions}
              getOptionLabel={(o) => o.nome}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              onChange={(_, val) => setCategoriaPai(val)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Categoria pai (opcional)"
                  placeholder="Raiz — sem categoria pai"
                  size="small"
                  sx={fieldSx}
                />
              )}
            />
          )}
        </Stack>
      </MDBox>

      <MDBox
        px={3}
        py={2}
        display="flex"
        justifyContent="flex-end"
        gap={1}
        borderTop="1px solid"
        borderColor="grey.200"
      >
        <MDButton variant="outlined" color="dark" onClick={onClose}>
          Cancelar
        </MDButton>
        <MDButton
          variant="gradient"
          color="dark"
          onClick={handleSave}
          disabled={saving || !nome.trim()}
          sx={saveButtonSx}
        >
          {saving ? "Salvando…" : "Salvar categoria"}
        </MDButton>
      </MDBox>
    </Dialog>
  );
}
